const DEFAULT_TIMEOUT_MS = 10_000;

/**
 * Drop-in replacement for fetch() that:
 *  - Wraps network failures in a user-visible error message
 *  - Aborts requests that exceed `timeoutMs` (default 10 s)
 *  - Never produces an unhandled rejection from network/timeout conditions
 *
 * The returned Response is identical to what native fetch() returns — callers
 * still check res.ok and call res.json() themselves.
 *
 * Error mapping:
 *  - Timeout     → "Request timed out — please try again"
 *  - Network     → "Network error — please check your connection and try again"
 *  - 4xx / 5xx  → caller responsibility (check res.ok)
 */
export async function safeFetch(
  url: string,
  options: RequestInit = {},
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, {
      ...options,
      signal: options.signal ?? controller.signal,
    });
    return res;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error("Request timed out — please try again");
    }
    throw new Error("Network error — please check your connection and try again");
  } finally {
    clearTimeout(timerId);
  }
}
